import React from 'react';
import { X, ChevronRight, Phone, MessageSquare, PackageCheck, ShoppingBag, ShieldCheck } from 'lucide-react';
import { STORE_CONFIG } from '../../config/storeConfig';
import { useStoreLogo, HEALTH_STORE_FALLBACK_LOGO } from '../../constants/branding';

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  activeNav: string;
  onNavigate: (navId: string) => void;
}

export const MobileMenu: React.FC<MobileMenuProps> = ({
  isOpen,
  onClose,
  activeNav,
  onNavigate,
}) => {
  const storeLogo = useStoreLogo();
  if (!isOpen) return null;

  const navLinks = [
    { id: 'home', label: 'Home' },
    { id: 'shop-all', label: 'Shop All' },
    { id: 'skincare', label: 'Skincare' },
    { id: 'hair-care', label: 'Hair Care' },
    { id: 'supplements', label: 'Supplements' },
    { id: 'bundles', label: 'Bundle Deals', badge: 'Save' },
    { id: 'blog', label: 'Beauty Blog' },
    { id: 'education-toys', label: 'Education & Toys' },
    { id: 'track-order', label: 'Track Order' },
  ];

  const handleLinkClick = (id: string) => {
    onNavigate(id);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden lg:hidden">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-xs transition-opacity duration-300"
        onClick={onClose}
        aria-hidden="true"
      />

      <div className="fixed inset-y-0 left-0 max-w-full flex">
        <div className="w-[85vw] max-w-sm bg-white shadow-2xl flex flex-col justify-between h-full">
          {/* Header */}
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <button
              onClick={() => {
                handleLinkClick('home');
              }}
              className="flex items-center text-left focus:outline-none cursor-pointer py-1"
              aria-label="Health Store Home"
            >
              <img
                src={storeLogo}
                alt="Health Store - Healthy Today, Stronger Tomorrow"
                width="260"
                height="60"
                className="h-12 w-auto max-w-[220px] object-contain select-none"
                onError={(e) => {
                  const target = e.currentTarget;
                  if (!target.src.includes('health_store_official_logo')) {
                    target.src = HEALTH_STORE_FALLBACK_LOGO;
                  }
                }}
              />
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
              aria-label="Close menu"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation Links */}
          <div className="flex-1 overflow-y-auto py-2 px-3 space-y-1">
            {navLinks.map((link) => {
              const isActive = activeNav === link.id;
              return (
                <button
                  key={link.id}
                  onClick={() => handleLinkClick(link.id)}
                  className={`w-full flex items-center justify-between px-3 py-3 rounded-xl text-left text-sm font-semibold transition-all ${
                    isActive
                      ? 'bg-[#191975] text-white'
                      : 'text-[#172554] hover:bg-slate-100'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    {link.label}
                    {link.badge && (
                      <span className="bg-rose-500 text-white text-[9px] font-extrabold px-1.5 py-0.5 rounded-full">
                        {link.badge}
                      </span>
                    )}
                  </span>
                  <ChevronRight
                    className={`w-4 h-4 ${isActive ? 'text-white' : 'text-slate-400'}`}
                  />
                </button>
              );
            })}
          </div>

          {/* Bottom Quick Contact Info */}
          <div className="p-4 border-t border-slate-100 bg-[#F8FAFC] space-y-3 text-xs">
            <div className="flex items-center gap-2 text-slate-600">
              <Phone className="w-3.5 h-3.5 text-[#191975]" />
              <span>Support: <strong>{STORE_CONFIG.phoneDisplay}</strong></span>
            </div>

            <a
              href={STORE_CONFIG.getWhatsAppUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white py-2.5 px-3 rounded-xl font-bold transition-colors shadow-xs"
            >
              <svg className="w-4 h-4 fill-white shrink-0" viewBox="0 0 24 24">
                <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
              </svg>
              <span>Chat on WhatsApp</span>
            </a>

            <div className="pt-1 flex items-center justify-center gap-1.5 text-[10px] text-slate-400 font-medium">
              <ShieldCheck className="w-3 h-3 text-emerald-500" />
              <span>Cash on Delivery across Pakistan</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
